import React, { useEffect, useState } from "react"
import {
    View,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
} from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FilledButtonM from "@common/components/button/FilledButtonM"
import FunnelChoiceChip from "@common/components/funnel/FunnelChoiceChip"
import { theme } from "@common/theme/theme"
import { elide } from "@common/utils/activationSuiteDisplay"
import InscriptionSheet from "../../inscription/components/InscriptionSheet"

/**
 * « Adapter le programme » — l'étape programme d'un stage vendu à la semaine.
 *
 * On ne sait pas où en est la classe de l'élève : le programme proposé est une
 * estimation, et c'est la famille qui sait ce qui manque. Un chapitre pas
 * encore vu n'est qu'un cas parmi d'autres (un chapitre vu qui manque, l'envie
 * de prendre de l'avance, un objectif précis) : d'où une phrase libre
 * plutôt que des choix d'objectif, et trois exemples pour donner l'idée.
 *
 * L'entrée vit EN HAUT du programme, là où le parent le regarde : une pastille
 * tant que rien n'est adapté, puis un bandeau qui cite sa phrase, avec
 * « Modifier » (le panneau se rouvre, pré-rempli) et « Revenir au programme
 * proposé ». La phrase s'écrit dans un panneau qui ne demande qu'elle ; la
 * recomposition, elle, se joue sur le programme lui-même (StagePlanScreen).
 *
 * Construit sur l'écran legacy du funnel par exception datée (TRANSITION.md).
 */

/** La phrase du parent : une phrase, pas une lettre (la même borne que le serveur). */
const PROGRAM_INSTRUCTIONS_MAX = 400

const EXAMPLES = [
    { label: "Déjà vu", text: "Déjà vu en classe : " },
    { label: "Pas encore vu", text: "Pas encore vu : " },
    { label: "Prendre de l'avance", text: "Prendre de l'avance sur " },
]

/** La pastille, ou le bandeau qui cite la phrase appliquée. */
export const StageProgramAdjustEntry = ({
    appliedInstructions,
    busy,
    onOpen,
    onReset,
}) => {
    if (!appliedInstructions) {
        return (
            <TouchableOpacity
                style={styles.pill}
                activeOpacity={0.8}
                onPress={onOpen}
                disabled={busy}
                accessibilityRole="button"
            >
                <Text style={styles.pillSpark}>✨</Text>
                <Text style={styles.pillLabel}>
                    Adapter le programme
                </Text>
            </TouchableOpacity>
        )
    }

    return (
        <View style={styles.banner}>
            <View style={styles.bannerHead}>
                <FontAwesome5
                    name="check-circle"
                    solid
                    size={14}
                    color={theme.colors.successDark}
                />
                <Text style={styles.bannerTitle}>Adapté à votre demande</Text>
            </View>
            <Text style={styles.bannerQuote} numberOfLines={2}>
                « {appliedInstructions} »
            </Text>
            <View style={styles.bannerActions}>
                <TouchableOpacity onPress={onOpen} disabled={busy}>
                    <Text style={styles.bannerLink}>Modifier</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={onReset} disabled={busy}>
                    <Text style={styles.bannerLink}>
                        Revenir au programme proposé
                    </Text>
                </TouchableOpacity>
            </View>
        </View>
    )
}

/** Le panneau : une phrase, trois exemples, « Recomposer ». */
const StageProgramAdjustSheet = ({
    isVisible,
    onClose,
    appliedInstructions,
    firstname,
    studentClass,
    previousClass,
    onRecompose,
}) => {
    const [draft, setDraft] = useState("")

    // À chaque ouverture, le champ repart de la phrase appliquée : « Modifier »
    // n'oblige pas à tout réécrire.
    useEffect(() => {
        if (isVisible) setDraft(appliedInstructions || "")
    }, [isVisible, appliedInstructions])

    const trimmed = draft.trim()
    const ready = !!trimmed && trimmed !== (appliedInstructions || "")
    // Un début de phrase garde son espace : le parent tape juste après.
    const addExample = (text) =>
        setDraft((current) =>
            (current.trim() ? `${current.trimEnd()} ${text}` : text).slice(
                0,
                PROGRAM_INSTRUCTIONS_MAX
            )
        )

    return (
        <InscriptionSheet
            isVisible={isVisible}
            onClose={onClose}
            title="Adapter le programme"
            closeLabel={null}
        >
            <Text style={styles.sheetHint}>
                Ce {elide(firstname || "votre enfant", "que")} a déjà vu, l'avance à prendre, un
                objectif précis : on recompose sa semaine.
            </Text>
            <TextInput
                value={draft}
                onChangeText={setDraft}
                multiline
                autoFocus
                maxLength={PROGRAM_INSTRUCTIONS_MAX}
                placeholder="Ex. : Pythagore déjà vu, de l'avance sur les fonctions"
                placeholderTextColor={theme.colors.grey}
                style={styles.input}
                accessibilityLabel="Votre demande"
            />
            <View style={styles.examples}>
                {EXAMPLES.map((example) => (
                    <FunnelChoiceChip
                        key={example.label}
                        ghost
                        label={example.label}
                        onPress={() => addExample(example.text)}
                    />
                ))}
            </View>
            <Text style={styles.fine}>
                {previousClass
                    ? `On choisit toujours dans le programme officiel de ${studentClass} et de ${previousClass}.`
                    : "On choisit toujours dans le programme officiel."}
            </Text>
            <FilledButtonM
                label="Recomposer"
                onPress={() => onRecompose(trimmed)}
                disabled={!ready}
                color={ready ? theme.colors.primary : theme.colors.lightMore}
                style={{ width: "100%", marginBottom: 0 }}
            />
        </InscriptionSheet>
    )
}

const styles = StyleSheet.create({
    pill: {
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 12,
        paddingVertical: 9,
        paddingHorizontal: 14,
        borderRadius: 999,
        borderWidth: 1.5,
        borderColor: theme.colors.primaryLight,
        backgroundColor: theme.colors.primaryLighter,
    },
    pillSpark: { fontSize: 14 },
    pillLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.primary,
    },
    banner: {
        gap: 6,
        marginBottom: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: theme.colors.successGray,
        backgroundColor: theme.colors.successLight,
    },
    bannerHead: { flexDirection: "row", alignItems: "center", gap: 8 },
    bannerTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 13,
        color: theme.colors.successDark,
    },
    bannerQuote: {
        fontFamily: "Montserrat-Regular",
        fontStyle: "italic",
        fontSize: 12.5,
        lineHeight: 17,
        color: theme.colors.primaryDark || theme.colors.primary,
    },
    bannerActions: { flexDirection: "row", flexWrap: "wrap", gap: 14 },
    bannerLink: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12.5,
        color: theme.colors.primary,
        textDecorationLine: "underline",
    },
    sheetHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12.5,
        lineHeight: 17,
        color: theme.colors.grey,
    },
    input: {
        minHeight: 96,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 12,
        paddingHorizontal: 12,
        paddingVertical: 10,
        fontFamily: "Montserrat-Regular",
        fontSize: 13.5,
        lineHeight: 19,
        color: theme.colors.primaryDark || theme.colors.primary,
        backgroundColor: theme.colors.white,
        textAlignVertical: "top",
    },
    examples: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
    fine: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        lineHeight: 15,
        color: theme.colors.grey,
    },
})

export default StageProgramAdjustSheet
